import { HttpEventType, HttpResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Input,
  ViewChild,
  inject,
  DestroyRef,
  input,
  ChangeDetectorRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { FileModel } from '@core/models/file.model';
import { InviteModel } from '@core/models/invite.model';
import { MessageModel } from '@core/models/message.model';
import { TaskModel } from '@core/models/task.model';
import * as fromRoot from '@core/redux';
import * as MessageActions from '@core/redux/message/message.actions';
import { FileService } from '@core/services/file.service';
import { getUser } from '@core/utils/getUser';
import { getUserId } from '@core/utils/getUserId';
import { select, Store } from '@ngrx/store';
import { PreviewFileComponent } from '@ux/components/preview-file/preview-file.component';
import { PreviewImageComponent } from '@ux/components/preview-image/preview-image.component';
import { UploadImageComponent } from '@ux/components/upload-image/upload-image.component';
import { AutoResizeDirective } from '@ux/directives/auto-resize.directive';
import * as _ from 'lodash';
import { NgClickOutsideDelayOutsideDirective } from 'ng-click-outside2';
import { Subject } from 'rxjs';

import { TaskViewCommentFormTimeComponent } from '../task-view-comment-form-time/task-view-comment-form-time.component';
import { TaskViewCommentsTimeComponent } from '../task-view-comments-time/task-view-comments-time.component';

interface FileResponse {
  id: string;
  originalName: string;
  file: string;
  projectId: string;
  isImage?: boolean;
  url?: string;
}

@Component({
  selector: 'app-task-view-comments-form',
  templateUrl: './task-view-comments-form.component.html',
  styleUrls: ['./task-view-comments-form.component.less'],
  imports: [
    ReactiveFormsModule,
    TaskViewCommentsTimeComponent,
    PreviewImageComponent,
    UploadImageComponent,
    PreviewFileComponent,
    TaskViewCommentFormTimeComponent,
    NgClickOutsideDelayOutsideDirective,
    AutoResizeDirective,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskViewCommentsFormComponent {
  private readonly store = inject<Store<fromRoot.State>>(Store);
  private readonly fileService = inject(FileService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  @ViewChild('inputUploadFiles') inputUploadFiles: any;

  task = input<TaskModel>();

  protected readonly messageControl = new FormControl('');

  @Input() newMessageSubject$: Subject<MessageModel>;

  messageOrigin: MessageModel;
  message: MessageModel;
  files = [];
  isOpenTimeForm = false;

  constructor() {
    this.message = new MessageModel();
    this.message.files = [];

    this.store
      .pipe(select(fromRoot.getMessageEdit), takeUntilDestroyed(this.destroyRef))
      .subscribe((message: MessageModel) => {
        if (message) {
          this.messageOrigin = Object.assign({}, message);
          this.message = Object.assign(new MessageModel(), message);
          this.message.files = [...(message.files || [])];
          this.messageControl.setValue(message.message || '');
          this.cdr.markForCheck();
        }
      });
  }

  onSubmit(event) {
    event.preventDefault();
    const messageText = this.messageControl.value;

    if (!messageText?.length) {
      return false;
    }

    this.message.message = messageText;

    if (this.message.id) {
      this.store.dispatch(new MessageActions.SaveAction(this.message));
    } else {
      const newMessage = Object.assign(new MessageModel(), {
        authorId: getUserId(),
        invite: Object.assign(new InviteModel(), {
          userId: getUserId(),
          user: getUser(),
        }),
        message: messageText,
        taskId: this.task().id,
        files: this.message.files,
        time: this.message.time,
        createdAt: new Date().toISOString(),
      });

      this.newMessageSubject$.next(newMessage);
      this.store.dispatch(new MessageActions.CreateAction(newMessage));
    }
    this._initMessageModel();
  }

  @HostListener('dragover', ['$event'])
  onDragOver(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  @HostListener('drop', ['$event'])
  onDrop(event) {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer.files.length) {
      this.onFileUpload(event.dataTransfer.files);
    }
  }

  onPasteFile(event: ClipboardEvent) {
    if (event.clipboardData?.files?.length) {
      event.preventDefault();
      this.onFileUpload(event.clipboardData.files);
    }
  }

  onFileUpload(event: Event | FileList) {
    let files: FileList;

    if (event instanceof Event) {
      const inputElement = event.target as HTMLInputElement;
      files = inputElement.files;
    } else {
      files = event;
    }

    if (!files || files.length === 0) {
      return;
    }

    // Убедимся, что files - это массив
    this.files = [...this.files];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      this.files.push({
        file: file,
        progress: 0,
        isError: false,
        error: null,
        index: i,
      });
    }

    if (event instanceof Event) {
      (event.target as HTMLInputElement).value = '';
    }

    this.files.forEach((fileObj, i) => {
      this.fileService
        .upload(fileObj.file, 'comment', this.task().id, null)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: event => {
            if (event.type === HttpEventType.UploadProgress) {
              fileObj.progress = Math.round((100 * event.loaded) / (event.total || 1));
              this.cdr.markForCheck();
            } else if (event instanceof HttpResponse) {
              fileObj.progress = 100;
              const file = event.body as FileResponse;
              // Создаем новый массив при добавлении файла
              this.message.files = [...this.message.files, file];
              const index = this.files.findIndex(f => f.file.name === fileObj.file.name);
              if (index !== -1) {
                this.files = [...this.files.slice(0, index), ...this.files.slice(index + 1)];
              }
              this.cdr.markForCheck();
            }
          },
          error: error => {
            fileObj.error = error.error?.file || 'Upload failed';
            fileObj.isError = true;
            this.cdr.markForCheck();
          },
        });
    });
  }

  onSetTime(time) {
    this.message.time = time;
    this.isOpenTimeForm = false;
  }

  onCloseTimeForm() {
    this.isOpenTimeForm = false;
  }

  onOpenTimeForm() {
    this.isOpenTimeForm = true;
  }

  onCloseEdit() {
    this.store.dispatch(new MessageActions.SetEditId(null));
    this._initMessageModel();
  }

  onClearTime() {
    this.isOpenTimeForm = false;
    this.message.time = null;
  }

  onStopUploadAndDeleteFile(file: any) {
    const index = _.findIndex(this.files, { name: file.name });
    this.files.splice(index, 1);
  }

  onDeleteFile(file: FileModel) {
    if (!this.message.files) {
      this.message.files = [];
      return;
    }

    const fileIndex = this.message.files.findIndex(f => f.id === file.id);
    if (fileIndex !== -1) {
      this.message.files = this.message.files.filter(f => f.id !== file.id);
    }

    this.fileService
      .delete(file.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.cdr.markForCheck();
        },
        error: error => {
          console.error('Ошибка при удалении файла:', error);
        },
      });
  }

  private _initMessageModel() {
    this.message = new MessageModel();
    this.message.files = [];
    this.message.taskId = this.task().id;
    this.messageOrigin = null;
    this.messageControl.setValue('');
  }
}
